import { TechnologiesClient } from '@/core/api/technologies-client'
import {
    DataFeedSource,
    EndState,
    RunState,
} from '@atbay/technology-service-node-sdk'
import { BaseService } from '@core/base/service/base-service'
import type { JobsResponseDataJob } from '@atbay/technology-service-node-sdk/lib/types/models/JobsResponseDataJob'

export const TechnologiesService =
    new (class TechnologiesService extends BaseService {
        private readonly client = TechnologiesClient
        private INTERVAL = 1000

        constructor() {
            super('technologies.service')
        }

        async getAllTechnologies() {
            const [error, result] = await this.request(() =>
                this.client.getAllTechnologies()
            )
            if (error) {
                return undefined
            }
            return result?.data?.data?.technologies || []
        }

        async addTechnologies(technologies: { id: string }[]) {
            const [error, result] = await this.request(() =>
                this.client.createAccountTechnologies(this.accountId, {
                    source: DataFeedSource.USER_INPUT,
                    technologies,
                })
            )
            if (error) {
                return false
            }

            return result?.data.data?.data_feed?.job_tracking_id
        }

        async getAccountJobs(): Promise<JobsResponseDataJob[]> {
            const [error, result] = await this.request(() =>
                this.client.getAccountJobs(this.accountId)
            )
            if (error) {
                return []
            }
            return result?.data.data.jobs || []
        }

        async getTechnologiesAddedStatus(
            jobTrackingIds: string[]
        ): Promise<[EndState[], Date | null]> {
            const [error, result] = await this.request(
                () =>
                    this.client.getJobsTracking(this.accountId, jobTrackingIds),
                {
                    poll: {
                        maxRetries: 10,
                        delay: this.INTERVAL,
                        delayMultiplier: 1.2,
                        shouldStop: (response) => {
                            const jobs =
                                response?.data.data?.jobs_tracking || []
                            return jobs.every(
                                (job) => job.run_state === RunState.ENDED
                            )
                        },
                    },
                }
            )

            const jobs = result?.data.data?.jobs_tracking || []

            if (error || !jobs.length) {
                return [Array(jobTrackingIds.length).fill(EndState.FAIL), null]
            }

            const latestEndTime = new Date(
                Math.max(
                    ...jobs.map((job) =>
                        new Date(job.updated_at as string).getTime()
                    )
                )
            )
            return [
                jobs.map((job) => job.end_state || EndState.FAIL),
                latestEndTime,
            ]
        }
    })()
